
import React from "react";

// ========================================
// PRODUCT TECHNICAL SPECIFICATIONS
// ========================================

const ProductTechnicalSpecifications = ({
    data = [],
    onChange
}) => {

    const specifications = Array.isArray(data)
        ? data
        : [];


    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const labelClass =
        "block text-sm font-medium text-gray-700 mb-2";

    const addButtonClass =
        "px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";


    // ========================================
    // SPECIFICATION MANAGEMENT
    // ========================================

    const addSpecification = () => {

        onChange([
            ...specifications,
            {
                name: "",
                value: "",
                unit: "",
                testStandard: "",
                conditions: "",
                notes: ""
            }
        ]);

    };


    const updateSpecification = (
        index,
        field,
        value
    ) => {

        onChange(
            specifications.map(
                (specification, i) =>
                    i === index
                        ? {
                            ...specification,
                            [field]: value
                        }
                        : specification
            )
        );

    };


    const removeSpecification = (index) => {

        onChange(
            specifications.filter(
                (_, i) => i !== index
            )
        );

    };


    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-6">

            {/* HEADER */}

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b pb-4">

                <div>

                    <h3 className="text-base font-semibold text-gray-900">
                        Technical Specifications
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                        Record measurable product properties,
                        technical values, testing standards and
                        relevant test conditions.
                    </p>

                </div>

                <button
                    type="button"
                    onClick={addSpecification}
                    className={addButtonClass}
                >
                    + Add Specification
                </button>

            </div>


            {/* EMPTY STATE */}

            {specifications.length === 0 && (

                <div className="py-12 text-center border border-dashed rounded-lg">

                    <p className="text-sm text-gray-500">
                        No technical specifications added yet.
                    </p>

                    <p className="text-xs text-gray-400 mt-2">
                        Add verified specifications from
                        technical data sheets or approved
                        product documentation.
                    </p>

                </div>

            )}


            {/* SPECIFICATIONS */}

            {specifications.map(
                (specification, specificationIndex) => (

                    <div
                        key={
                            specification._id ||
                            specificationIndex
                        }
                        className="border border-gray-200 rounded-lg overflow-hidden"
                    >

                        {/* SPECIFICATION HEADER */}

                        <div className="flex justify-between items-center gap-3 bg-gray-100 px-5 py-4">

                            <div>

                                <h4 className="font-semibold text-gray-900">
                                    Specification {specificationIndex + 1}
                                </h4>

                                {specification.name && (

                                    <p className="text-xs text-gray-500 mt-1">
                                        {specification.name}
                                    </p>

                                )}

                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    removeSpecification(
                                        specificationIndex
                                    )
                                }
                                className={removeButtonClass}
                            >
                                Remove Specification
                            </button>

                        </div>


                        {/* SPECIFICATION FIELDS */}

                        <div className="p-5 space-y-5">

                            {/* NAME AND VALUE */}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                                <div>

                                    <label className={labelClass}>
                                        Specification Name *
                                    </label>

                                    <input
                                        type="text"
                                        value={specification.name || ""}
                                        onChange={(event) =>
                                            updateSpecification(
                                                specificationIndex,
                                                "name",
                                                event.target.value
                                            )
                                        }
                                        placeholder="e.g. Compressive Strength"
                                        className={inputClass}
                                    />

                                </div>

                                <div>

                                    <label className={labelClass}>
                                        Specification Value *
                                    </label>

                                    <input
                                        type="text"
                                        value={specification.value || ""}
                                        onChange={(event) =>
                                            updateSpecification(
                                                specificationIndex,
                                                "value",
                                                event.target.value
                                            )
                                        }
                                        placeholder="e.g. 40"
                                        className={inputClass}
                                    />

                                </div>

                            </div>


                            {/* UNIT AND TEST STANDARD */}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                                <div>

                                    <label className={labelClass}>
                                        Unit
                                    </label>

                                    <input
                                        type="text"
                                        value={specification.unit || ""}
                                        onChange={(event) =>
                                            updateSpecification(
                                                specificationIndex,
                                                "unit",
                                                event.target.value
                                            )
                                        }
                                        placeholder="e.g. MPa"
                                        className={inputClass}
                                    />

                                </div>

                                <div>

                                    <label className={labelClass}>
                                        Test Standard
                                    </label>

                                    <input
                                        type="text"
                                        value={specification.testStandard || ""}
                                        onChange={(event) =>
                                            updateSpecification(
                                                specificationIndex,
                                                "testStandard",
                                                event.target.value
                                            )
                                        }
                                        placeholder="e.g. Applicable ASTM or IS standard"
                                        className={inputClass}
                                    />

                                </div>

                            </div>


                            {/* TEST CONDITIONS */}

                            <div>

                                <label className={labelClass}>
                                    Test Conditions
                                </label>

                                <textarea
                                    value={specification.conditions || ""}
                                    onChange={(event) =>
                                        updateSpecification(
                                            specificationIndex,
                                            "conditions",
                                            event.target.value
                                        )
                                    }
                                    placeholder="Describe relevant testing conditions, curing period, temperature or other factors."
                                    rows={3}
                                    className={inputClass}
                                />

                            </div>


                            {/* NOTES */}

                            <div>

                                <label className={labelClass}>
                                    Additional Notes
                                </label>

                                <textarea
                                    value={specification.notes || ""}
                                    onChange={(event) =>
                                        updateSpecification(
                                            specificationIndex,
                                            "notes",
                                            event.target.value
                                        )
                                    }
                                    placeholder="Additional technical information or clarification."
                                    rows={3}
                                    className={inputClass}
                                />

                            </div>

                        </div>

                    </div>

                )
            )}


            {/* GUIDANCE */}

            <div className="border-t pt-4">

                <p className="text-xs text-gray-500">
                    Enter specifications only when supported by
                    approved technical documentation. Include
                    test standards and conditions wherever available
                    so the AI agent can present technical values
                    accurately and in context.
                </p>

            </div>

        </div>

    );

};

export default ProductTechnicalSpecifications;
